<script setup>
import misc404 from '@images/pages/404.png'
import misc401 from '@images/pages/401.png'
import { STATUS_IMAGE, resolveErrorView } from '~/utils/statusPages'
import { DEFAULT_LANDING } from '~/utils/authNavigation'

const props = defineProps({
  error: {
    type: Object,
    required: true,
  },
})

defineOptions({
  inheritAttrs: false,
})

// Nuxt 3 / Vite: use import.meta.dev instead of process.dev
const isDev = import.meta.dev

// The whole error view (title / description / illustration / retry) is a pure
// mapping of the status code — see `~/utils/statusPages` (unit-tested). The
// shared StatusPageScaffold owns the layout (and reads the breakpoint via
// `$vuetify.display`, since Vuetify's `useDisplay()` is unavailable in the
// special error.vue context).
const view = computed(() => resolveErrorView(props.error, { isDev }))

const errorContent = computed(() => view.value)

const errorImage = computed(() =>
  view.value.image === STATUS_IMAGE.UNAUTHORIZED ? misc401 : misc404,
)

const showTryAgain = computed(() => view.value.showTryAgain)

// Page title for browser tab
useHead({
  title: () => `${errorContent.value.title} - ABAIntake`,
})

function goHome() {
  // The app's single home/landing constant (used by the auth middleware too);
  // when a dashboard lands, changing DEFAULT_LANDING moves this button with it.
  clearError({ redirect: DEFAULT_LANDING })
}

function tryAgain() {
  clearError({ redirect: false })
  window?.location?.reload()
}
</script>

<template>
  <NuxtLayout name="blank">
    <StatusPageScaffold
      :status-code="error.statusCode"
      :title="errorContent.title"
      :description="errorContent.description"
      :image="errorImage"
    >
      <template #actions>
        <!-- Secondary LEFT (only for a retriable error), primary RIGHT. -->
        <VBtn
          v-if="showTryAgain"
          variant="tonal"
          @click="tryAgain"
        >
          <VIcon
            start
            icon="tabler-refresh"
          />
          Try Again
        </VBtn>

        <VBtn
          color="primary"
          @click="goHome"
        >
          <VIcon
            start
            icon="tabler-home"
          />
          Back to Home
        </VBtn>
      </template>
    </StatusPageScaffold>
  </NuxtLayout>
</template>
